import React, { useState, useMemo } from 'react'
import Select from 'react-select'
import { checkLocale } from '../../utils'
import Image from '../Image'
import Link from '../Link'

import DropdownIndicator from '../DownloadDropdownIndicator'

import {
  partnerListContainer,
  partnerListFilters,
  partnerListFilterItem,
  partnerListContent,
  partnerListCard,
  partnerListCardLogoWrapper,
  partnerListCardLogo,
  partnerListCardDetails,
  partnerListNoResult,
  partnerListCardItem,
  partnerListNoResultWrapper,
  partnerListBackgroundColor,
} from './index.module.css'

const PartnerList = ({
  sortableComponentNoResultsText,
  sortableData,
  sortableFieldItems,
  sortableComponentSortableFields,
  nodeLocale,
}) => {
  const variables = {}
  const FilterLabelsMapping = {}

  for (let index = 0; index < sortableComponentSortableFields.length; index++) {
    variables[sortableComponentSortableFields[index]] = ''
    FilterLabelsMapping[sortableComponentSortableFields[index]] =
      sortableComponentSortableFields[index]
  }

  const [filter, setFilter] = useState(variables)

  // Styles for select list
  const customStyles = useMemo(
    () => ({
      option: (styles, { isSelected, isFocused, isDisabled }) => {
        let backgroundColor = '#FFFFFF'
        let color = '#000000'
        let borderColor = '#C6C6C6'

        if (isSelected) {
          backgroundColor = '#FFFFFF'
          color = '#000000'
          borderColor = '#000000'
        }

        if (isFocused) {
          backgroundColor = '#FFFFFF'
          color = '#000000'
        }

        if (isDisabled) {
          backgroundColor = '#FFFFFF'
          color = '#C6C6C6'
        }

        return {
          ...styles,
          width: '100%',
          opacity: 1,
          border: '1px solid #C6C6C6',
          borderColor,
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: '14px',
          backgroundColor,
          color,
          marginTop: 'px',
          '&:active': {
            // Overwrittes the different states of border
            background: '#fff',
          },
        }
      },
      control: (styles) => {
        return {
          ...styles,
          minHeight: 52,
          boxShadow: 'none',
          borderColor: '#5E79FB',
        }
      },
      menu: (styles) => {
        return {
          ...styles,
          width: 300,
          paddingTop: 0,
          marginTop: '-1px',
          minHeight: 52,
          boxShadow: 'none',
        }
      },
      menuList: (styles) => {
        return { ...styles, paddingTop: 0, paddingBottom: 0, minHeight: 52 }
      },
      singleValue: (styles, state) => {
        return { ...styles, marginLeft: '7px' }
      },
      indicatorSeparator: (styles, state) => {
        return { ...styles, border: 'none', backgroundColor: '#fff' }
      },
      indicatorContainer: (styles, state) => {
        return {
          ...styles,
          border: 'none',
          backgroundColor: '#fff',
          flexDirection: 'column',
        }
      },
    }),
    []
  )

  const handleChangeFilter = (filterType) => (values) => {
    setFilter({
      ...filter,
      [filterType]: values,
    })
  }

  const filteredData = useMemo(() => {
    return sortableData.filter(({ node }) => {
      return Object.keys(filter).every((key) => {
        if (!filter[key]) return true
        return node[key]?.some((item) => {
          return item === filter[key].value
        })
      })
    })
  }, [sortableData, filter])

  const sortFilteredData = useMemo(
    () => (arr) => {
      return arr
        .map(({ node }) => {
          if (node.order === null) {
            node['order'] = 1e9
          }
          return { node: { ...node } }
        })
        .sort((a, b) => a.node.title.localeCompare(b.node.title))
        .sort((a, b) => a.node.order - b.node.order)
    },
    []
  )

  const splitVal = (arr, local) => {
    return arr.map((a) => checkLocale(a, local))
  }

  const translateHeader = (header, local, fields = []) => {
    const val = fields?.filter((field) => {
      return field?.node?.id === header
    })
    const node = val ? val[0]?.node?.name : null
    return checkLocale(node, local)
  }

  const imageStyles = {
    objectFit: 'contain',
    objectPosition: '50% 50%',
  }

  const getLink = (partner) => {
    let url

    if (partner.link) {
      url = partner.link
      return url
    }

    if (partner.pageblock === null) {
      url = `#`
      return url
    }

    url = partner?.pageblock.filter(({ page }) => page)[0].page[0].slug

    let nodeLocaleLowerCase = nodeLocale.toLowerCase()

    if (url) {
      switch (nodeLocaleLowerCase) {
        case 'en-us':
          url = url
          break
        case 'zh-cn':
          url = '/zh-cn' + url
          break
        case 'ja-jp':
          url = '/jp' + url
          break
        default:
          url = '#'
          break
      }
    } else {
      url = '#'
    }

    return url
  }

  return (
    <div className={partnerListContainer}>
      <div className={partnerListFilters}>
        {sortableFieldItems?.map(({ node: fieldItems }) => {
          const options = fieldItems?.items?.validations[0]?.in?.map(
            (item) => ({
              value: item,
              label: checkLocale(item, nodeLocale),
            })
          )
          return (
            <div key={fieldItems.id} className={partnerListFilterItem}>
              <h4>{checkLocale(fieldItems.name, nodeLocale)}</h4>
              <Select
                isSearchable={false}
                value={filter[fieldItems.id]}
                options={options}
                placeholder={checkLocale(
                  'Please select...|请选择...',
                  nodeLocale
                )}
                styles={customStyles}
                autoFocus={false}
                components={{ DropdownIndicator }}
                onChange={handleChangeFilter(fieldItems.id)}
                isClearable={true}
              />
            </div>
          )
        })}
      </div>
      <div className={partnerListBackgroundColor}>
        <div
          className={`${partnerListContent} ${
            sortFilteredData(filteredData).length === 0
              ? partnerListNoResultWrapper
              : ''
          }`}
        >
          {sortFilteredData(filteredData).length > 0 ? (
            sortFilteredData(filteredData).map(({ node: partner }) => {
              return (
                <Link key={partner.id} to={getLink(partner)}>
                  <div className={partnerListCard}>
                    {partner.logo && (
                      <div className={partnerListCardLogoWrapper}>
                        <Image
                          classes={partnerListCardLogo}
                          source={partner?.logo}
                          imageStyles={imageStyles}
                        />
                      </div>
                    )}
                    <div className={partnerListCardDetails}>
                      {partner.title && <h3>{partner.title}</h3>}
                      {Object.entries(FilterLabelsMapping)?.map(
                        ([key, value]) => {
                          return (
                            value &&
                            partner[key] && (
                              <div className={partnerListCardItem} key={key}>
                                <h4>
                                  {translateHeader(
                                    value,
                                    nodeLocale,
                                    sortableFieldItems
                                  )}
                                </h4>
                                <p>
                                  {splitVal(partner[key], nodeLocale).join(
                                    ', '
                                  )}
                                </p>
                              </div>
                            )
                          )
                        }
                      )}
                    </div>
                  </div>
                </Link>
              )
            })
          ) : (
            <p className={partnerListNoResult}>
              {sortableComponentNoResultsText}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default PartnerList
